import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import API from '../backend/api';
import EditApplicationModal from '../components/EditApplicationModal';
import DiscardConfirmModal from '../components/DiscardConfirmModal';
import './Dashboard.css';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [allApplications, setAllApplications] = useState([]); // For metrics
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [editingApplication, setEditingApplication] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [applicationToDiscard, setApplicationToDiscard] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const statusConfig = {
    submitted: {
      label: 'Submitted',
      color: '#007bff',
      icon: '📝',
      description: 'Applications you\'ve submitted'
    },
    interview: {
      label: 'Interview',
      color: '#ffc107',
      icon: '🎯',
      description: 'Interview invitations and scheduled calls'
    },
    assessment: {
      label: 'Assessment',
      color: '#17a2b8',
      icon: '📊',
      description: 'Technical assessments and coding challenges'
    },
    offer: {
      label: 'Offer',
      color: '#28a745',
      icon: '🎉',
      description: 'Job offers received'
    },
    rejected: {
      label: 'Rejected',
      color: '#dc3545',
      icon: '❌',
      description: 'Applications that were rejected'
    },
    withdrawn: {
      label: 'Withdrawn',
      color: '#6c757d',
      icon: '↩️',
      description: 'Applications you withdrew from'
    }
  };

  useEffect(() => {
    if (user) {
      fetchApplications();
    }
  }, [user]);

  useEffect(() => {
    let filtered = allApplications.length > 0 ? allApplications : applications;
    
    // Apply status filter
    if (selectedStatus) {
      filtered = filtered.filter(app => app.status === selectedStatus);
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(app => 
        app.job.title.toLowerCase().includes(query) ||
        app.job.company.name.toLowerCase().includes(query) ||
        (app.job.location && app.job.location.toLowerCase().includes(query)) ||
        (app.notes && app.notes.toLowerCase().includes(query))
      );
    }
    
    setFilteredApplications(filtered);
  }, [selectedStatus, searchQuery, applications, allApplications]);

  const fetchApplications = async (page = 1) => {
    try {
      setLoading(true);
      console.log(`Fetching applications page ${page}...`);
      
      const response = await API.get(`/applications/?page=${page}`, {
        headers: {
          Authorization: `Bearer ${user.accessToken}`
        }
      });
      
      const data = response.data;
      console.log('Applications response:', data);
      
      // Handle paginated response
      if (data.results) {
        setApplications(data.results);
        setTotalCount(data.count);
        setTotalPages(Math.ceil(data.count / 20)); // 20 is PAGE_SIZE
        setCurrentPage(page);
        
        // Fetch all for metrics (in background)
        fetchAllForMetrics();
      } else {
        // Non-paginated response (fallback)
        const apps = Array.isArray(data) ? data : [];
        setApplications(apps);
        setAllApplications(apps);
        setTotalCount(apps.length);
        setTotalPages(1);
      }
      
    } catch (err) {
      console.error('Error fetching applications:', err);
      console.error('Error details:', err.response?.data);
      setError(`Failed to load applications: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllForMetrics = async () => {
    // Fetch all applications in background for accurate metrics
    try {
      let allApps = [];
      let page = 1;
      let hasMore = true;
      
      while (hasMore && page <= 10) { // Max 10 pages (200 apps) for performance
        const response = await API.get(`/applications/?page=${page}`, {
          headers: {
            Authorization: `Bearer ${user.accessToken}`
          }
        });
        
        if (response.data.results) {
          allApps = [...allApps, ...response.data.results];
          hasMore = !!response.data.next;
          page++;
        } else {
          hasMore = false;
        }
      }
      
      setAllApplications(allApps);
    } catch (err) {
      console.error('Error fetching all applications for metrics:', err);
      // Use current page for metrics if fetch fails
      setAllApplications(applications);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      fetchApplications(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleDiscardClick = (application) => {
    setApplicationToDiscard(application);
    setShowDiscardModal(true);
  };

  const handleConfirmDiscard = async () => {
    if (!applicationToDiscard) return;

    try {
      await API.delete(`/applications/${applicationToDiscard.id}/`, {
        headers: {
          Authorization: `Bearer ${user.accessToken}`
        }
      });

      // Remove the application from local state immediately (no page reload)
      setApplications(prev => prev.filter(app => app.id !== applicationToDiscard.id));
      setAllApplications(prev => prev.filter(app => app.id !== applicationToDiscard.id));
      setTotalCount(prev => prev - 1);
      
      setMessage('Application discarded successfully');
      setShowDiscardModal(false);
      setApplicationToDiscard(null);
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting application:', err);
      setError(`Failed to discard application: ${err.response?.data?.detail || err.message}`);
      setShowDiscardModal(false);
      setApplicationToDiscard(null);
      
      // Clear error after 5 seconds
      setTimeout(() => setError(''), 5000);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      fetchApplications(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const createSampleData = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Creating sample data...');
      const response = await API.post('/sample-data/', {}, {
        headers: {
          Authorization: `Bearer ${user.accessToken}`
        }
      });
      console.log('Sample data response:', response.data);
      setMessage(`Created ${response.data.count} sample applications!`);
      // Refresh applications after creating sample data
      await fetchApplications();
    } catch (err) {
      console.error('Error creating sample data:', err);
      setError(`Failed to create sample data: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusCounts = () => {
    const counts = {};
    Object.keys(statusConfig).forEach(status => {
      // Use allApplications for accurate counts across all pages
      counts[status] = allApplications.filter(app => app.status === status).length;
    });
    return counts;
  };

  const getTotalApplications = () => {
    // Use totalCount from API or allApplications length
    return totalCount || allApplications.length;
  };

  const getRecentApplications = () => {
    return [...allApplications]
      .sort((a, b) => new Date(b.date_applied) - new Date(a.date_applied))
      .slice(0, 5);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleStatusClick = (status) => {
    if (selectedStatus === status) {
      setSelectedStatus(null); // Deselect if clicking the same status
    } else {
      setSelectedStatus(status);
    }
  };

  const handleEditClick = (app) => {
    setEditingApplication(app);
    setShowEditModal(true);
  };

  const handleSaveApplication = async (appId, updatedData) => {
    try {
      const response = await API.patch(`/applications/${appId}/`, updatedData, {
        headers: {
          Authorization: `Bearer ${user.accessToken}`
        }
      });
      
      // Update the application in state
      setApplications(prev => 
        prev.map(app => app.id === appId ? response.data : app)
      );
      
      setMessage('✓ Application updated successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating application:', error);
      throw error;
    }
  };

  const clearFilter = () => {
    setSelectedStatus(null);
  };

  const clearAllFilters = () => {
    setSelectedStatus(null);
    setSearchQuery('');
  };

  if (!user) {
    return (
      <div className="dashboard-container">
        <div className="login-required">
          <h2>Dashboard</h2>
          <p>Please log in to view your job application dashboard.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading your applications...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={fetchApplications} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Removed the full-page success message - now using inline banner below

  const statusCounts = getStatusCounts();
  const totalApplications = getTotalApplications();
  const recentApplications = getRecentApplications();

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Job Application Dashboard</h1>
        <p>Track and manage your job applications</p>
        {applications.length === 0 && (
          <div className="sample-data-section">
            <p>No applications found. Create some sample data to get started:</p>
            <button 
              onClick={createSampleData}
              disabled={loading}
              className="sample-data-button"
            >
              {loading ? 'Creating...' : 'Create Sample Data'}
            </button>
          </div>
        )}
      </div>

      {/* Success Message Banner */}
      {message && (
        <div className="success-banner" style={{
          background: '#d4edda',
          border: '1px solid #c3e6cb',
          color: '#155724',
          padding: '12px 20px',
          borderRadius: '8px',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.5em' }}>✅</span>
            <span style={{ fontWeight: '500' }}>{message}</span>
          </div>
          <button 
            onClick={() => setMessage('')}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#155724',
              cursor: 'pointer',
              fontSize: '1.2em',
              padding: '0 5px'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Overview Stats */}
      <div className="overview-section">
        <div className="total-applications">
          <div className="stat-card total">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <div className="stat-number">{totalApplications}</div>
              <div className="stat-label">Total Applications</div>
            </div>
          </div>
        </div>

        <div className="status-overview">
          {Object.entries(statusConfig).map(([status, config]) => (
            <div
              key={status}
              className={`status-card ${selectedStatus === status ? 'selected' : ''}`}
              onClick={() => handleStatusClick(status)}
              style={{ '--status-color': config.color }}
            >
              <div className="status-icon">{config.icon}</div>
              <div className="status-content">
                <div className="status-number">{statusCounts[status]}</div>
                <div className="status-label">{config.label}</div>
                <div className="status-description">{config.description}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-container">
          <input
            type="text"
            placeholder="🔍 Search by company, position, location, or notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="clear-search">
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Filter Controls */}
      {(selectedStatus || searchQuery) && (
        <div className="filter-controls">
          <div className="active-filter">
            <span>
              {selectedStatus && `Showing: ${statusConfig[selectedStatus].label} applications`}
              {selectedStatus && searchQuery && ' | '}
              {searchQuery && `Search: "${searchQuery}"`}
            </span>
            <button onClick={clearAllFilters} className="clear-filter">
              ✕ Clear All Filters
            </button>
          </div>
        </div>
      )}

      {/* Applications List */}
      <div className="applications-section">
        <div className="section-header">
          <h2>
            {selectedStatus ? `${statusConfig[selectedStatus].label} Applications` : 'All Applications'}
            <span className="count">({filteredApplications.length})</span>
          </h2>
          {!selectedStatus && (
            <a href="/add-application" className="add-application-btn">
              + Add Application
            </a>
          )}
        </div>

        {filteredApplications.length > 0 ? (
          <>
            <div className="applications-grid">
              {filteredApplications.map((app) => (
              <div key={app.id} className="application-card">
                <div className="app-header">
                  <div className="app-title">
                    <h3>{app.job.title}</h3>
                    <p className="company">{app.job.company.name}</p>
                  </div>
                  <div className="app-status">
                    <span 
                      className={`status-badge ${app.status}`}
                      style={{ 
                        borderColor: statusConfig[app.status]?.color,
                        color: statusConfig[app.status]?.color 
                      }}
                    >
                      {statusConfig[app.status]?.icon} {statusConfig[app.status]?.label}
                    </span>
                  </div>
                </div>
                
                <div className="app-details">
                  <div className="detail-row">
                    <span className="label">Applied:</span>
                    <span className="value">{formatDate(app.date_applied)}</span>
                  </div>
                  
                  {app.job.location && (
                    <div className="detail-row">
                      <span className="label">Location:</span>
                      <span className="value">{app.job.location}</span>
                    </div>
                  )}
                  
                  {app.notes && (
                    <div className="detail-row notes">
                      <span className="label">Notes:</span>
                      <span className="value">{app.notes}</span>
                    </div>
                  )}
                </div>

                <div className="app-actions">
                  {app.job.posting_url && (
                    <a 
                      href={app.job.posting_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="action-btn view-job"
                    >
                      View Job
                    </a>
                  )}
                  <button 
                    className="action-btn edit"
                    onClick={() => handleEditClick(app)}
                  >
                    Edit
                  </button>
                  <button 
                    className="action-btn delete"
                    onClick={() => handleDiscardClick(app)}
                    title="Discard this application"
                  >
                    🗑️ Discard
                  </button>
                </div>
              </div>
            ))}
            </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <button 
                    className="pagination-btn"
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                  >
                    ← Previous
                  </button>
                  
                  <div className="pagination-info">
                    <span className="page-indicator">
                      Page {currentPage} of {totalPages}
                    </span>
                    <span className="items-info">
                      Showing {applications.length} of {totalCount} applications
                    </span>
                  </div>
                  
                  <button 
                    className="pagination-btn"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          ) : (
          <div className="no-applications">
            <div className="no-apps-content">
              <div className="no-apps-icon">
                {selectedStatus ? statusConfig[selectedStatus].icon : '📝'}
              </div>
              <h3>
                {selectedStatus 
                  ? `No ${statusConfig[selectedStatus].label} Applications` 
                  : 'No Applications Yet'
                }
              </h3>
              <p>
                {selectedStatus 
                  ? `You don't have any ${statusConfig[selectedStatus].label.toLowerCase()} applications yet.`
                  : 'Start tracking your job applications by adding your first one.'
                }
              </p>
              <a href="/add-application" className="add-application-btn primary">
                + Add Application
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Recent Applications (when no filter) */}
      {!selectedStatus && recentApplications.length > 0 && (
        <div className="recent-section">
          <h2>Recent Applications</h2>
          <div className="recent-list">
            {recentApplications.map((app) => (
              <div key={app.id} className="recent-item">
                <div className="recent-info">
                  <span className="recent-title">{app.job.title}</span>
                  <span className="recent-company">at {app.job.company.name}</span>
                </div>
                <div className="recent-meta">
                  <span className="recent-date">{formatDate(app.date_applied)}</span>
                  <span 
                    className="recent-status"
                    style={{ color: statusConfig[app.status]?.color }}
                  >
                    {statusConfig[app.status]?.label}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <EditApplicationModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingApplication(null);
        }}
        application={editingApplication}
        onSave={handleSaveApplication}
      />
      
      <DiscardConfirmModal
        isOpen={showDiscardModal}
        onClose={() => {
          setShowDiscardModal(false);
          setApplicationToDiscard(null);
        }}
        onConfirm={handleConfirmDiscard}
        applicationName={applicationToDiscard ? `${applicationToDiscard.job.title} at ${applicationToDiscard.job.company.name}` : ''}
      />
    </div>
  );
};

export default Dashboard;
